import { Client } from '@connectrpc/connect';

import { UserConfirmationService } from './espb/user_confirmation_service_pb';
import { knownEnumOrDefault, requireFieldNotEmpty } from './grpcUtils';
import { toSyncDto } from './sync';
import {
  UserConfirmationResultType,
  UserConfirmationType,
  UserConfirmationId,
  SyncUpdate,
  UserConfirmationData,
  SessionId,
} from './types';

export default class UserConfirmationServiceClient {
  constructor(private readonly espbClient: Client<typeof UserConfirmationService>) {}

  async create({
    type,
    loginId = '',
    vaultId = '',
    profileId = '',
    organizationId = '',
  }: UserConfirmationData): Promise<{
    id: UserConfirmationId;
    syncUpdate: SyncUpdate;
    webauthnOptionsJson: string | undefined;
  }> {
    const resp = await this.espbClient.create({
      data: { type, loginId, vaultId, profileId, organizationId },
    });
    return {
      id: requireFieldNotEmpty(resp.id),
      syncUpdate: toSyncDto(requireFieldNotEmpty(resp.syncUpdate)),
      webauthnOptionsJson: resp.webauthnOptionsJson || undefined,
    };
  }

  async get(id: UserConfirmationId): Promise<{
    fallbackString: string;
    requestingSessionId: SessionId;
    data: UserConfirmationData;
  }> {
    const resp = await this.espbClient.get({ id });
    const data = requireFieldNotEmpty(resp.data);
    return {
      fallbackString: resp.fallbackString,
      requestingSessionId: resp.requestingSessionId,
      data: {
        type: knownEnumOrDefault(data.type, UserConfirmationType),
        vaultId: data.vaultId || undefined,
        loginId: data.loginId || undefined,
        profileId: data.profileId || undefined,
        organizationId: data.organizationId || undefined,
      },
    };
  }

  async sendResult(
    id: UserConfirmationId,
    type: UserConfirmationResultType,
    webauthnResponseJson: string | undefined,
  ): Promise<SyncUpdate> {
    const resp = await this.espbClient.sendResult({ id, type, webauthnResponseJson });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async retrieveResult(id: UserConfirmationId): Promise<{
    resultType: UserConfirmationResultType;
  }> {
    const resp = await this.espbClient.retrieveResult({ id });
    return {
      resultType: knownEnumOrDefault(resp.resultType, UserConfirmationResultType),
    };
  }

  async cancel(id: UserConfirmationId): Promise<SyncUpdate> {
    const resp = await this.espbClient.cancel({ id });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }
}
